import { useState } from "react";
import * as RouteHelper from "@utils/routes";
import { Link, matchPath, useLocation } from "react-router-dom";

// material-ui
import { styled, useTheme, Theme } from "@mui/material/styles";
import {
  AppBar,
  Box,
  Container,
  CssBaseline,
  Toolbar,
  useMediaQuery,
} from "@mui/material";

// project imports
import Header from "./Header";
import Sidebar from "./Sidebar";
import HorizontalBar from "./HorizontalBar";
import Breadcrumbs from "@stories/ui-component/extended/Breadcrumbs";

import { getNavMenuItems } from "./menu-items";
import LAYOUT_CONST, { drawerWidth } from "constant";
import useConfig from "@hooks/utils/useConfig";

// assets
import { IconChevronRight } from "@tabler/icons";
import { ProfileProvider } from "@hooks/context/useProfileContext";
import {
  ProjectProvider,
  useProjectContext,
} from "@hooks/context/useProjectContext";

function useRouteMatch(patterns: readonly string[]) {
  const { pathname } = useLocation();

  for (let i = 0; i < patterns.length; i += 1) {
    const pattern = patterns[i];
    const possibleMatch = matchPath(pattern, pathname);
    if (possibleMatch !== null) {
      return possibleMatch;
    }
  }

  return null;
}

interface MainStyleProps {
  theme: Theme;
  open: boolean;
  layout: string;
}

// styles
const Main = styled("main", { shouldForwardProp: (prop) => prop !== "open" })(
  ({ theme, open, layout }: MainStyleProps) => ({
    ...theme.typography.mainContent,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    ...(!open && {
      transition: theme.transitions.create("margin", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.shorter + 200,
      }),
      [theme.breakpoints.up("md")]: {
        marginLeft:
          layout === LAYOUT_CONST.VERTICAL_LAYOUT
            ? -(drawerWidth - 72)
            : "20px",
        width: `calc(100% - ${drawerWidth}px)`,
        marginTop: layout === LAYOUT_CONST.HORIZONTAL_LAYOUT ? 135 : 88,
      },
      [theme.breakpoints.down("md")]: {
        marginLeft: "20px",
        width: `calc(100% - ${drawerWidth}px)`,
        padding: "16px",
        marginTop: 88,
      },
      [theme.breakpoints.down("sm")]: {
        marginLeft: "10px",
        width: `calc(100% - ${drawerWidth}px)`,
        padding: "16px",
        marginRight: "10px",
        marginTop: 88,
      },
    }),
    ...(open && {
      // 'margin 538ms cubic-bezier(0.4, 0, 1, 1) 0ms',
      transition: theme.transitions.create("margin", {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.shorter + 200,
      }),
      marginLeft: layout === LAYOUT_CONST.HORIZONTAL_LAYOUT ? "20px" : 0,
      marginTop: layout === LAYOUT_CONST.HORIZONTAL_LAYOUT ? 135 : 88,
      width: `calc(100% - ${drawerWidth}px)`,
      [theme.breakpoints.up("md")]: {
        marginTop: layout === LAYOUT_CONST.HORIZONTAL_LAYOUT ? 135 : 88,
      },
      [theme.breakpoints.down("md")]: {
        marginLeft: "20px",
        marginTop: 88,
      },
      [theme.breakpoints.down("sm")]: {
        marginLeft: "10px",
        marginTop: 88,
      },
    }),
  })
);

// ==============================|| MAIN LAYOUT ||============================== //
interface MainDashboardLayoutProps {
  children: React.ReactNode;
}
const MainDashboardLayout = ({ children }: MainDashboardLayoutProps) => {
  const theme = useTheme();
  const { selectedProject, isOwner } = useProjectContext();
  const matchDownMd = useMediaQuery(theme.breakpoints.down("md"));

  const routeMatch = useRouteMatch([
    RouteHelper.listProjects,
    RouteHelper.contractorDashboard(":projectId"),
    RouteHelper.packages(":projectId"),
    RouteHelper.analyticsLabourHours(":projectId"),
    RouteHelper.analyticsSpend(":projectId"),
    RouteHelper.contractorEmployees(":projectId"),
    RouteHelper.labourHours(":projectId", ":packageId"),
    RouteHelper.socialProcurement(":projectId", ":packageId"),
    RouteHelper.contractorReports(":projectId"),
    RouteHelper.directory(":projectId"),
  ]);

  let selectedId = routeMatch?.pattern?.path.split("/")[1] || "NO_ID_SELECTED";

  if (selectedId === "analytics") {
    if (routeMatch?.pattern?.path.split("/")[2] === "labourHours") {
      selectedId = "labour-hours";
    } else if (routeMatch?.pattern?.path.split("/")[2] === "spend") {
      selectedId = "spend";
    }
  }

  const [isDrawOpen, setIsDrawOpen] = useState(true);
  const [selectedItem, setSelectedItem] = useState<string[]>([selectedId]);

  const { container, layout } = useConfig();

  const header = (
    <Toolbar
      sx={{
        paddingLeft: "17px !important",
        paddingRight: "17px !important",
      }}
    >
      <Header setIsDrawOpen={setIsDrawOpen} isDrawOpen={isDrawOpen} />
    </Toolbar>
  );

  return (
    <ProfileProvider>
      <ProjectProvider>
        <Box sx={{ display: "flex" }}>
          <CssBaseline />
          {/* header */}
          <AppBar
            enableColorOnDark
            position="fixed"
            color="inherit"
            elevation={0}
            sx={{ bgcolor: theme.palette.background.default }}
          >
            {header}
          </AppBar>

          {/* horizontal menu-list bar */}
          {layout === LAYOUT_CONST.HORIZONTAL_LAYOUT && !matchDownMd && (
            <HorizontalBar isDrawOpen={isDrawOpen} />
          )}

          {/* drawer */}
          {(layout === LAYOUT_CONST.VERTICAL_LAYOUT || matchDownMd) && (
            <Sidebar
              isDrawOpen={isDrawOpen}
              selectedItem={selectedItem}
              setIsDrawOpen={setIsDrawOpen}
              setSelectedItem={setSelectedItem}
            />
          )}

          {/* main content */}
          <Main theme={theme} open={isDrawOpen} layout={layout}>
            <Container
              maxWidth={container ? "lg" : false}
              {...(!container && { sx: { px: { xs: 0 } } })}
            >
              {/* breadcrumb */}
              <Breadcrumbs
                separator={IconChevronRight}
                navigation={getNavMenuItems(selectedProject.id, isOwner)}
                icon
                title
                rightAlign
              />
              {children}
            </Container>
          </Main>
        </Box>
      </ProjectProvider>
    </ProfileProvider>
  );
};

export default MainDashboardLayout;
